import { AfterViewInit, Component, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppState, ShowRequest } from '../../../core';
import { AppTab } from '../../../core/app-tab';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { ScoutTaskService } from '../../../core/services';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { ScoutPathPointsTableComponent } from './path-points-table/path-points-table.component'
import { ScoutBuildingPointsTableComponent } from './building-points-table/building-points-table.component'
import { BehaviorSubject, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PathPointParametersTableComponent } from '../scout-path-report/path-point-parameters-table/path-point-parameters-table.component';
import { ScoutDataTableBase } from './scout-data-table/scout-data-table-base';
import { TableViewFilter, TableViewQueryCriteria } from '../../../shared/components/table-view/table-view-component-base';



@Component({
  selector: 'app-scout-path-entry-report',
  templateUrl: './scout-path-entry-report.component.html',
  styleUrls: ['./scout-path-entry-report.component.scss']
})
export class ScoutPathEntryReportComponent extends FesBaseComponent implements AfterViewInit {

  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() public detailFn : (vi: ShowRequest) => void;
  @Input() set tabConfig( tabConf : AppTab ) { 
    tabConf.setSelected = this.onTabSelected
    this._tabConf = tabConf    
  }
  _tabConf : AppTab

  @Input() set contextJson( strJson: string) {
    this.parseContextJson(strJson)
  }
  @Output('contextJsonChange') get contextJsonChange() {
    return this.contextChangeEmitter
  }
  contextChangeEmitter = new EventEmitter<string>()

  @ViewChild('buildingPointsTable') buildingPointsTable : ScoutBuildingPointsTableComponent
  @ViewChild('pathPointsTable') pathPointsTable : ScoutPathPointsTableComponent
  @ViewChild('pathPointParamTable') pathPointParamTable : PathPointParametersTableComponent;
  
  pathIds : string[] = []
  pathNumbers : number[] = []
  contextInitializedSubject = new BehaviorSubject<boolean>(false)

  constructor(appState:AppState, 
      public scoutTaskService: ScoutTaskService,
      public modalService: BsModalService,) {
    super(appState);
  }

  ngAfterViewInit(): void {
    timer(0).subscribe(_=>this.contextInitializedSubject.next(true))
  }

  pathNumberToPathName(i:number) {
    return i==null ? '' : this.scoutTaskDoc.buildingId + 'P' + `${i}`.padStart(2,'0')
  }

  pathIdsToRegEx(pathIds: string[] = null){
    if ( !pathIds ) {
      pathIds = this.pathIds
    }
    if ( !pathIds.length ) {
      return ''
    } else {
      const pathNumbers = this.scoutTaskDoc.scoutPaths.filter(p=>p.pathNumber&&pathIds.includes(p.id)).map(p=>p.pathNumber)
      return 'P(' + pathNumbers.map(pathNumber=>`${pathNumber}`.padStart(2,'0')).join('|') + ')$'
    }
  }

  // hoopla for keeping state in browser url
  parseContextJson(strJson) {
    //console.log(`parseContextJson(${strJson})`)
    try {
      if (strJson) {
        const context = JSON.parse(strJson)
        this.pathIds = context.pathIds
        if ( !this.pathIds?.length ) {
          this.pathIds = this.scoutTaskDoc.scoutPaths.map(sp=>sp.id)
        }
        this.updatePathNumbers()
        let pathNames : string[] = []
        this.pathNumbers?.forEach( pathNumber => {
          const pathName = this.pathNumberToPathName(pathNumber)
          pathNames.push( pathName )
        })

        const initChildContext = ( childName:string, context) => {
          console.log(`initChildContext(${childName})`)
          if ( !context[childName] ) {
            context[childName] = {}
          }
          let childContext : TableViewQueryCriteria = context[childName]
          if ( !childContext.filters ) {
            childContext.filters = []
          }
          childContext.filters = childContext.filters.filter(f=>f.key != 'pathName')
          childContext.filters.push( { key: 'pathName', mode: '~*', value: this.pathIdsToRegEx()} )
        }
        this.doWhenViewInitialized( () => {
          initChildContext('pathPointsTable',context)
          this.pathPointsTable.setContext(context.pathPointsTable) 
          initChildContext('buildingPointsTable',context)
          this.buildingPointsTable.setContext(context.buildingPointsTable)
          this.pathPointParamTable.populate()
        } )
      }
    } catch ( error ) {
      this.appState.publishError(error, this)
    }    
  }

  onChildContextChange( srcStrContext: string, srcTable : ScoutDataTableBase<any> ) {
    let srcContext = srcTable.getContext();
    const destTable = srcTable == this.pathPointsTable ? this.buildingPointsTable : this.pathPointsTable
    let destContext = destTable.getContext()
    const pathNameFilter = srcContext.filters?.find(f=>f.key=='pathName')
    if ( !srcContext.filters ) {
      srcContext.filters = []
    }
    if ( !destContext.filters ) {
      destContext.filters = []
    }
    const removeFilterByName = (name:string,filters:TableViewFilter[]) => {
      const existingIndex = filters.findIndex(f=>f.key==name)
      if ( existingIndex != -1 ) {
        filters.splice(existingIndex,1)
      }
      return filters
    }
    removeFilterByName('pathName',destContext.filters)
    destContext.filters.push(pathNameFilter)
    destTable.setContext(destContext)
    destTable.reload()

    // Rebuild the path id list so url will reflect the pathName filter of children
    this.pathIds = this.scoutTaskDoc.scoutPaths.filter( (path)=>{
      if ( !pathNameFilter ) {
        return true
      } else {
        const pathName = this.pathNumberToPathName(path.pathNumber)
        const regEx = new RegExp(pathNameFilter.value)
        const bMatch = regEx.test(pathName)
        return bMatch
      }
    }).map(path=>path.id)

    removeFilterByName('pathName',destContext.filters)
    removeFilterByName('pathName',srcContext.filters)
    const context = {
      pathPointsTable: srcTable == this.pathPointsTable ? srcContext: destContext,
      buildingPointsTable: srcTable == this.buildingPointsTable ? srcContext: destContext,
      pathIds: this.pathIds
    }
    this.pathPointParamTable.populate(this.pathIds)
    const strContext = JSON.stringify(context)
    //console.log(strContext)
    this.contextChangeEmitter.emit(strContext)
  }

  getReportTitle = () => {
    let title = 'Scout Data - ' + this.scoutTaskDoc.buildingId
    if ( this.taskScoutPathsCnt > this.pathNumbers.length) {
      title += ` #${this.pathNumbers.join(',')}`      
    }
    return title
  }

  taskScoutPathsCnt = 0
  updatePathNumbers = () => {
    this.pathNumbers = []
    const paths = this.scoutTaskDoc?.scoutPaths
    this.taskScoutPathsCnt = paths.length
    for( const path of paths ) {
      if ( path.pathNumber && this.pathIds.includes(path.id) ) {
        this.pathNumbers.push(path.pathNumber)
      }      
    }
    this.pathNumbers.sort((a,b)=>a-b)
    this._tabConf?.viewInfo?.title?.next(this.getReportTitle())
  }


  onTabSelected = ( bSelected : boolean ) => {
    const tabSelectFn = () => {
      console.log('onTabSelected('+bSelected+')')
      this.pathPointsTable.onTabSelected(bSelected)
      this.buildingPointsTable.onTabSelected(bSelected)
      this.updatePathNumbers()
    }
    this.doWhenViewInitialized( tabSelectFn )
  }


  doWhenViewInitialized( fn : () => void ) {
    this.contextInitializedSubject.pipe( filter(bReady=>!!bReady), take(1) ).subscribe(_=>fn())
  }

}
